import React, { useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import instance from "../../api/instance";
import { connect_img } from "../../assets/images/images";
import "../../pages/Features/Features.css";
import LoaderApply from "../CommonComponents/Loader/LoaderApply";
import "../../pages/Home/New_Home.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { sendEnquiry } from "../../api/apiRoutes";

/** Aligned with JobDetails form patterns: clear copy, max lengths, no double spaces, strict formats. */
const LIMITS = {
  full_name: { max: 100 },
  email: { max: 254 },
  phone: { minDigits: 10, maxDigits: 15 },
  company_name: { max: 200 },
  designation: { max: 120 },
  message: { min: 10, max: 2000 },
};

const REQUIRED_FIELDS = [
  "full_name",
  "email",
  "phone",
  "message",
  "company_name",
  "designation",
  "company_size",
];

/** Pipedrive lead-capture webhook (n8n). Fire-and-forget: must never break the enquiry submit. */
const LEAD_WEBHOOK_URL =
  "https://marketing-n8n.arcitech.in/webhook/arcitech-form-lead";
const LEAD_BRAND = "Haire";

/** <select> submits the option value ("1".."4"); n8n/Pipedrive needs the human-readable label. */
const COMPANY_SIZE_LABELS = {
  1: "10-1000 employees",
  2: "2000 - 5000 employees",
  3: "5000 - 10000 employees",
  4: "10000+ employees",
};

/**
 * Mirrors the enquiry to the marketing n8n webhook with the exact keys it expects.
 * Isolated try/catch — a webhook failure must never affect the primary submit.
 */
function sendLeadToWebhook(formData) {
  try {
    const payload = {
      name: formData.full_name,
      email: formData.email,
      phone: formData.phone,
      company: formData.company_name,
      designation: formData.designation,
      companysize:
        COMPANY_SIZE_LABELS[formData.company_size] || formData.company_size,
      notes: formData.message,
      Brand: LEAD_BRAND,
      "Page URL": typeof window !== "undefined" ? window.location.href : "",
    };

    // keepalive so the request survives page navigation; no await (fire-and-forget).
    fetch(LEAD_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((err) => console.error("Lead webhook failed:", err));
  } catch (err) {
    console.error("Lead webhook failed:", err);
  }
}

function hasDoubleSpace(value) {
  return typeof value === "string" && /\s{2,}/.test(value);
}

/**
 * Returns an error message string, or null when valid.
 * Single source of truth for blur/onChange and submit (same as Ant Design `rules` consolidation).
 */
function validateFieldValue(name, value) {
  const raw = value == null ? "" : String(value);
  const trimmed = raw.trim();

  switch (name) {
    case "full_name": {
      if (!trimmed) return "Please enter your full name";
      if (hasDoubleSpace(raw)) {
        return "Double spaces are not allowed in the full name";
      }
      if (trimmed.length > LIMITS.full_name.max) {
        return `Full name cannot exceed ${LIMITS.full_name.max} characters`;
      }
      if (!/^[a-zA-Z\s.'-]+$/.test(trimmed)) {
        return "Full name should only contain letters, spaces, periods, hyphens, and apostrophes";
      }
      return null;
    }
    case "phone": {
      if (!trimmed) return "Please enter your contact number";
      const digitCount = (trimmed.match(/\d/g) || []).length;
      if (
        digitCount < LIMITS.phone.minDigits ||
        digitCount > LIMITS.phone.maxDigits
      ) {
        return "Please enter a valid contact number (10 to 15 digits)";
      }
      if (!/^[+]?[\d\s().-]+$/.test(trimmed)) {
        return "Contact number contains invalid characters";
      }
      return null;
    }
    case "email": {
      if (!trimmed) return "Please enter your email address";
      if (trimmed.length > LIMITS.email.max) {
        return `Email cannot exceed ${LIMITS.email.max} characters`;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        return "Please enter a valid email address";
      }
      return null;
    }
    case "company_name": {
      if (!trimmed) return "Please enter your company name";
      if (hasDoubleSpace(raw)) {
        return "Double spaces are not allowed in the company name";
      }
      if (trimmed.length > LIMITS.company_name.max) {
        return `Company name cannot exceed ${LIMITS.company_name.max} characters`;
      }
      if (!/^[a-zA-Z0-9\s.,&'()-]+$/.test(trimmed)) {
        return "Company name contains invalid characters";
      }
      return null;
    }
    case "designation": {
      if (!trimmed) return "Please enter your designation";
      if (hasDoubleSpace(raw)) {
        return "Double spaces are not allowed in the designation";
      }
      if (trimmed.length > LIMITS.designation.max) {
        return `Designation cannot exceed ${LIMITS.designation.max} characters`;
      }
      if (!/^[a-zA-Z0-9\s.,'()/&+#-]+$/.test(trimmed)) {
        return "Designation contains invalid characters";
      }
      return null;
    }
    case "company_size": {
      if (!trimmed) return "Please select company size";
      return null;
    }
    case "message": {
      if (!trimmed) return "Please tell us how we can help";
      if (trimmed.length < LIMITS.message.min) {
        return `Please enter at least ${LIMITS.message.min} characters`;
      }
      if (trimmed.length > LIMITS.message.max) {
        return `Message cannot exceed ${LIMITS.message.max} characters`;
      }
      return null;
    }
    default:
      return null;
  }
}

const GetInTouch = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: "",
    company_name: "",
    designation: "",
    company_size: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    const message = validateFieldValue(name, value);
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[name] = message;
      else delete next[name];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name] || String(value).trim().length > 0) {
      validateField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const validateAllFields = () => {
    const next = {};
    REQUIRED_FIELDS.forEach((field) => {
      const msg = validateFieldValue(field, formData[field]);
      if (msg) next[field] = msg;
    });
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = {};
    REQUIRED_FIELDS.forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    const validationErrors = validateAllFields();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await instance.post(sendEnquiry, formData);

      if (response.status === 200 || response.status === 201) {
        // Mirror lead to marketing n8n -> Pipedrive. Fire-and-forget; never blocks success.
        sendLeadToWebhook(formData);

        toast.success("Submitted successfully", {
          position: toast.POSITION.TOP_END,
        });

        setFormData({
          full_name: "",
          email: "",
          phone: "",
          message: "",
          company_name: "",
          designation: "",
          company_size: "",
        });
        setErrors({});
        setTouched({});
      } else {
        console.error("Validation Errors:", response.data);
      }
    } catch (error) {
      if (error.response) {
        console.error("Error:", error.response.data);
      } else if (error.request) {
        console.error("Error:", error.request);
      } else {
        console.error("Error:", error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <section className="get_in_touch new_get_sec">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="get_in_touch_inner">
                <div
                  className="get_in_touch_sec_left_new wow animate__slideInLeft"
                  data-wow-duration="1.3s"
                >
                  <h6>We are here to help you!</h6>
                  <p>
                    Please leave your details and our team will contact you
                    shortly.
                  </p>
                  <a href="mailto:support@haire.ai" className="email-link">
                    <span>
                      <FontAwesomeIcon icon={faEnvelope} />
                    </span>
                    support@haire.ai
                  </a>
                </div>

                <div
                  className="get_in_touch_inner_left wow animate__slideInRight"
                  data-wow-duration="2s"
                >
                  <div className="get_in_touch_inner_left_form_sec ">
                    <form
                      onSubmit={handleSubmit}
                      className="get_touch"
                      noValidate
                    >
                      <div className="get_in_touch_inner_left_form_sec_grid">
                        <div className="get_in_touch_inner_left_form_sec_single">
                          <label>
                            Full Name{" "}
                            <span className="required_asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Full Name"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            maxLength={LIMITS.full_name.max}
                            autoComplete="name"
                            className={errors.full_name ? "error-input" : ""}
                          />
                          {errors.full_name && (
                            <span className="error">{errors.full_name}</span>
                          )}
                        </div>
                        <div className="get_in_touch_inner_left_form_sec_single">
                          <label>
                            Contact No
                            <span className="required_asterisk">*</span>
                          </label>
                          <input
                            type="tel"
                            placeholder="Enter Contact No"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyPress={(e) => {
                              if (!/[0-9+\-().\s]/.test(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            maxLength={22}
                            autoComplete="tel"
                            className={errors.phone ? "error-input" : ""}
                            title="10 to 15 digits; you may use +, spaces, or dashes."
                          />
                          {errors.phone && (
                            <span className="error">{errors.phone}</span>
                          )}
                        </div>
                      </div>

                      <div className="get_in_touch_inner_left_form_sec_grid">
                        <div className="get_in_touch_inner_left_form_sec_single">
                          <label>
                            Email Address
                            <span className="required_asterisk">*</span>
                          </label>
                          <input
                            type="email"
                            placeholder="Email Address"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            maxLength={LIMITS.email.max}
                            autoComplete="email"
                            className={errors.email ? "error-input" : ""}
                          />
                          {errors.email && (
                            <span className="error">{errors.email}</span>
                          )}
                        </div>
                        <div className="get_in_touch_inner_left_form_sec_single">
                          <label>
                            Company Name
                            <span className="required_asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Company Name"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            maxLength={LIMITS.company_name.max}
                            autoComplete="organization"
                            className={errors.company_name ? "error-input" : ""}
                          />
                          {errors.company_name && (
                            <span className="error">{errors.company_name}</span>
                          )}
                        </div>
                      </div>

                      <div className="get_in_touch_inner_left_form_sec_grid">
                        <div className="get_in_touch_inner_left_form_sec_single">
                          <label>
                            Designation
                            <span className="required_asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Designation"
                            name="designation"
                            value={formData.designation}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            maxLength={LIMITS.designation.max}
                            autoComplete="organization-title"
                            className={errors.designation ? "error-input" : ""}
                          />
                          {errors.designation && (
                            <span className="error">{errors.designation}</span>
                          )}
                        </div>
                        <div className="get_in_touch_inner_left_form_sec_single">
                          <label>
                            Company Size
                            <span className="required_asterisk">*</span>
                          </label>
                          <select
                            name="company_size"
                            value={formData.company_size}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={errors.company_size ? "error-input" : ""}
                          >
                            <option value="">Select company size</option>
                            <option value="1">10-1000 employees</option>
                            <option value="2">2000 - 5000 employees</option>
                            <option value="3">5000 - 10000 employees</option>
                            <option value="4">10000+ employees</option>
                          </select>
                          {errors.company_size && (
                            <span className="error">{errors.company_size}</span>
                          )}
                        </div>
                      </div>

                      <div className="get_in_touch_inner_left_form_sec_single custom-textarea-landing">
                        <label>
                          Tell Us How We Can Help
                          <span className="required_asterisk">*</span>
                        </label>
                        <textarea
                          rows={4}
                          placeholder="Tell Us How We Can Help"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          maxLength={LIMITS.message.max}
                          className={errors.message ? "error-input" : ""}
                        />
                        {errors.message && (
                          <span className="error">{errors.message}</span>
                        )}
                        <span
                          className="char-count"
                          style={{
                            display: "block",
                            textAlign: "right",
                            fontSize: "12px",
                            marginTop: "4px",
                            color: formData.message.length >= LIMITS.message.max ? "#dc2626" : "#9ca3af",
                          }}
                        >
                          {formData.message.length} / {LIMITS.message.max}
                        </span>
                      </div>

                      <div className="get-in-touch__submit-row">
                        <button
                          type="submit"
                          className="try-button get-in-touch__send-btn"
                          disabled={isLoading}
                        >
                          {isLoading && (
                            <LoaderApply
                              style={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                transform: "translate(-50%, -50%)",
                              }}
                            />
                          )}
                          {!isLoading && "Send"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                <div
                  className="get_in_touch_inner_right"
                  style={{ display: "none" }}
                >
                  <div className="get_in_touch_inner_right_img">
                    <img src={connect_img} alt="" />
                  </div>
                  <div className="get_in_touch_inner_right_single">
                    <span>
                      <i className="fas fa-envelope"></i>
                    </span>
                    <a href="mailto:support@haire.ai">support@haire.ai</a>
                  </div>
                  <div className="get_in_touch_inner_right_single">
                    <span>
                      <i className="fas fa-location-dot"></i>
                    </span>
                    <a
                      href="https://maps.app.goo.gl/GZTKxuprex47w5Fr5"
                      className="address-one"
                    >
                      Trade World, Kamala Mills, C-208, near Lodha Park, Lower
                      Parel, Mumbai, Maharashtra 400013
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default GetInTouch;
